import React, { useCallback, useEffect, useState } from "react";
import { FeatherPlus } from "@subframe/core";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import {
  fetchCurrencies,
  type Currency,
} from "../../../../shared/data/api/currenciesApi";
import { CurrencyDialog } from "./CurrencyDialog";

/**
 * Columns: code, name, symbol, decimals. Code is fixed and narrow because it is
 * always three letters; the name takes the flexible share because it is the
 * only column whose length varies.
 */
const COLUMNS = "sm:grid-cols-[4rem_minmax(0,1fr)_4rem_5rem]";

/**
 * NO EXCHANGE RATE COLUMN. `currencies.exchange_rate` exists and reads
 * 0.000000 on every row, because nothing writes it — rates are fetched live
 * from Zoho by `BaseController::getExchangeRateCached` and kept in the app
 * cache, never stored. Showing the column would print "0" against every
 * currency and call it a rate. Same trap as the bank balances.
 */
const CurrencyRow: React.FC<{ currency: Currency; fresh: boolean }> = ({
  currency,
  fresh,
}) => (
  <div
    className={`flex w-full flex-col gap-1 border-b border-solid border-neutral-border px-4 py-3 last:border-b-0 sm:grid sm:items-center sm:gap-4 ${COLUMNS} ${
      fresh ? "bg-brand-50" : ""
    }`}
  >
    <span className="min-w-0 text-monospace-body font-monospace-body text-default-font">
      {currency.currency_code}
    </span>

    <span className="flex min-w-0 flex-wrap items-center gap-2">
      <span className="text-body font-body text-default-font break-words">
        {currency.currency_name}
      </span>
      {/* The one currency the books are actually kept in. Fixed when the Zoho
          organisation was created and not changeable afterwards, so it is a
          fact to show rather than a control. */}
      {Number(currency.is_base_currency) === 1 && (
        <Badge variant="brand">Base</Badge>
      )}
      {fresh && <Badge variant="neutral">Just enabled</Badge>}
    </span>

    <span className="min-w-0 text-body font-body text-default-font">
      {currency.currency_symbol || "—"}
    </span>

    {/* Number(), not ===. The endpoint returns the raw column, so
        price_precision arrives as the STRING "0" or "2" despite the DTO
        typing it as a number, and a strict compare against 0 silently fell
        through to "0 decimals" for JPY. */}
    <span className="min-w-0 text-body font-body text-subtext-color">
      {Number(currency.price_precision) === 0
        ? "No decimals"
        : `${Number(currency.price_precision)} decimals`}
    </span>
  </div>
);

/**
 * The currencies this organisation can transact in.
 *
 * "Enabled" is Zoho's own model: an organisation switches on the currencies it
 * needs, and every amount elsewhere carries one of these ids. Adding one writes
 * to Zoho first, because `currency_id` is minted over there and is what
 * `bank_accounts`, `customers` and `vendors` all reference.
 *
 * No delete. Zoho refuses to remove a currency anything points at, and the ones
 * an organisation has usually are pointed at — offering a button that mostly
 * fails is worse than not offering it. Remove it in Zoho Books, then sync.
 */
export const CurrenciesList: React.FC = () => {
  const [currencies, setCurrencies] = useState<Currency[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [freshCode, setFreshCode] = useState<string | null>(null);

  const load = useCallback(
    async (markNew = false) => {
      const before = new Set((currencies ?? []).map((c) => c.currency_code));
      try {
        const result = await fetchCurrencies();
        setCurrencies(result.data);
        setLoadError(null);
        if (markNew) {
          const added = result.data.find((c) => !before.has(c.currency_code));
          setFreshCode(added?.currency_code ?? null);
        }
      } catch (err) {
        setLoadError(
          err instanceof Error ? err.message : "Could not load the currencies.",
        );
      }
    },
    [currencies],
  );

  useEffect(() => {
    let live = true;
    fetchCurrencies()
      .then((result) => live && setCurrencies(result.data))
      .catch(
        (err: unknown) =>
          live &&
          setLoadError(
            err instanceof Error
              ? err.message
              : "Could not load the currencies.",
          ),
      );
    return () => {
      live = false;
    };
  }, []);

  const enabledCodes = new Set((currencies ?? []).map((c) => c.currency_code));

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex w-full flex-wrap items-center justify-between gap-3">
        <span className="text-body font-body text-subtext-color">
          {currencies === null
            ? "Reading the currencies..."
            : `${currencies.length} enabled`}
        </span>
        <Button
          variant="brand-primary"
          size="small"
          icon={<FeatherPlus />}
          onClick={() => setAddOpen(true)}
          disabled={currencies === null}
        >
          Add currency
        </Button>
      </div>

      {loadError !== null && (
        <div className="flex w-full items-start rounded-lg border border-solid border-red-200 bg-red-50 p-3">
          <span className="text-body font-body text-red-600">{loadError}</span>
        </div>
      )}

      {notice !== null && (
        <div className="flex w-full items-start rounded-lg border border-solid border-green-200 bg-green-50 p-3">
          <span className="text-body font-body text-green-800">{notice}</span>
        </div>
      )}

      {currencies !== null && currencies.length > 0 && (
        <div className="flex w-full flex-col rounded-md border border-solid border-neutral-border">
          <div
            className={`hidden w-full gap-4 border-b border-solid border-neutral-border bg-neutral-50 px-4 py-2 sm:grid ${COLUMNS}`}
          >
            <span className="text-caption-bold font-caption-bold text-subtext-color">
              Code
            </span>
            <span className="text-caption-bold font-caption-bold text-subtext-color">
              Currency
            </span>
            <span className="text-caption-bold font-caption-bold text-subtext-color">
              Symbol
            </span>
            <span className="text-caption-bold font-caption-bold text-subtext-color">
              Decimals
            </span>
          </div>

          {currencies.map((currency) => (
            <CurrencyRow
              key={currency.currency_id}
              currency={currency}
              fresh={currency.currency_code === freshCode}
            />
          ))}
        </div>
      )}

      <CurrencyDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        enabledCodes={enabledCodes}
        onCreated={(message) => {
          setNotice(message);
          void load(true);
        }}
      />
    </div>
  );
};
